import { useMemo } from 'react';
import { ResourceGrid } from './ResourceGrid';
import type { TagFilter } from './TagFilterBuilder';
import { useCollections } from '../hooks/useCollections';
import { useProject } from '../hooks/useProject';
import { hasPermission } from '../types/project';
import { createBrowseDatasource } from '../services/resourceDatasource';

/**
 * BrowseResults — browses resources matching a tag filter across all project
 * collections. Shared by the Tag Explorer (/browse) and batch review (/home/batch),
 * which differ only in how the filter is produced. `filter` is the tag-filter object
 * ({size, projectId, tags:{and,or,not}}) serialized into the FindAll tagFilter param.
 */
export function BrowseResults({
  projectId,
  filter,
  onFilterChange,
  editableFilter,
  testId,
}: {
  projectId: string;
  filter: object;
  /** When provided, the grid toolbar shows an editable tag filter. */
  onFilterChange?: (f: TagFilter) => void;
  /** The user-editable part of `filter` shown in the builder (defaults to `filter`). */
  editableFilter?: TagFilter | null;
  testId?: string;
}) {
  const { data: collections, isLoading } = useCollections(projectId);
  const { data: project } = useProject(projectId);

  const datasource = useMemo(() => {
    if (!collections || collections.length === 0) return null;
    return createBrowseDatasource({
      projectId,
      collectionIds: collections.map((c) => c.id),
      tagFilter: JSON.stringify(filter),
    });
  }, [projectId, collections, filter]);

  const bulkContext = useMemo(
    () =>
      collections
        ? {
            projectId,
            projectName: project?.name,
            collectionIds: collections.map((c) => c.id),
            filter, // the tag filter; all-mode tags everything matching it
            canManageTags: !!project && hasPermission(project, 'MANAGE_TAGS'),
            canCreateBatches: !!project && hasPermission(project, 'MANAGE_BATCHES'),
            canManageReports: !!project && hasPermission(project, 'MANAGE_REPORTS'),
          }
        : undefined,
    [projectId, collections, filter, project]
  );

  if (isLoading) return <p className="text-muted">Loading…</p>;
  if (!datasource) return <p className="text-muted">This project has no collections to browse.</p>;
  return (
    <ResourceGrid
      datasource={datasource}
      bulkContext={bulkContext}
      testId={testId}
      tagFilter={onFilterChange ? { projectId, filter: editableFilter !== undefined ? editableFilter : (filter as TagFilter), onApply: onFilterChange } : undefined}
    />
  );
}
